import { SharedSpinner } from '@components';
import { useTenantProfileWorkStations } from '@hooks';
import { Select } from 'antd';
import { observer } from 'mobx-react';
import React, { useMemo } from 'react';

interface IProps {
  style?: React.CSSProperties;
  cleanName?: boolean;
  placeholder?: string;
  mode?: 'multiple' | 'tags';
  status?: 'error' | 'warning';
  stickyHeader?: boolean;
  value?: string | string[] | undefined;
  allowClear?: boolean;
  disabled?: boolean;
  defaultValue?: { label: string; value: string }[];
  onClear?: () => void;
  onSelect?: (id: string) => void;
  onDeselect?: (id: string) => void;
}

export const WorkStationSelect: React.FC<IProps> = observer(
  ({
    mode,
    style,
    placeholder = 'Workstation select',
    status,
    stickyHeader,
    value,
    allowClear = true,
    defaultValue,
    disabled = false,
    onClear,
    onSelect,
    onDeselect,
  }) => {
    const { workStations, loading } = useTenantProfileWorkStations();

    const options = useMemo(() => {
      const defaultOptions =
        defaultValue?.map((item) => ({
          label: item.label,
          value: item.value,
        })) || [];

      const serviceOptions = workStations.map((workStation) => ({
        label: workStation.name,
        value: workStation.id,
      }));

      // Combine the options and filter duplicates
      const uniqueOptionsMap = new Map();
      [...defaultOptions, ...serviceOptions].forEach((option) => {
        uniqueOptionsMap.set(`${option.label}-${option.value}`, option);
      });

      const allOptions = Array.from(uniqueOptionsMap.values());

      // Sort the options alphabetically by label
      allOptions.sort((a, b) => a.label.localeCompare(b.label));

      // Move selected values to the top
      if (value) {
        const selectedValues = Array.isArray(value) ? value : [value];
        const selectedOptions = allOptions.filter((option) =>
          selectedValues.includes(option.value)
        );
        const otherOptions = allOptions.filter((option) => !selectedValues.includes(option.value));
        return [...selectedOptions, ...otherOptions];
      }

      return allOptions;
    }, [workStations, defaultValue, value]);

    return (
      <Select
        showSearch
        allowClear={allowClear}
        status={status}
        style={style}
        mode={mode}
        placeholder={placeholder}
        onClear={onClear}
        onSelect={onSelect}
        onDeselect={onDeselect}
        filterOption={(input, option) => option?.label.toLowerCase().includes(input.toLowerCase())}
        value={value}
        options={options}
        loading={loading}
        disabled={disabled}
        getPopupContainer={(stickyHeader && ((trigger: any) => trigger.parentElement)) || undefined}
        dropdownRender={(menu) => (
          <div>
            {menu}
            {loading && (
              <div style={{ textAlign: 'center', padding: '8px 0' }}>
                <SharedSpinner loading={loading} />
              </div>
            )}
          </div>
        )}
      />
    );
  }
);
